/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ToggleCard, ToggleCardEdit, ToggleCardSummary } from '@/components/toggle-card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Typography } from '@/components/typography';
import type { CheckoutActionData } from '../types';
import type { ShopperBasketsV2 } from '@salesforce/storefront-next-runtime/scapi';
import CheckoutErrorBanner from './checkout-error-banner';
import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { formatCurrency } from '@/lib/currency';
import { useShippingOptions } from './use-shipping-options';

interface ShippingOptionsProps {
    onSubmit: (formData: FormData) => void;
    isLoading: boolean;
    actionData?: CheckoutActionData;
    shippingMethods?: ShopperBasketsV2.schemas['ShippingMethodResult'];
    isCompleted: boolean;
    isEditing: boolean;
    onEdit: () => void;
}

export default function ShippingOptions({
    onSubmit,
    isLoading,
    actionData,
    shippingMethods,
    isCompleted: _isCompleted,
    isEditing,
    onEdit,
}: ShippingOptionsProps) {
    const { currency } = useSite();
    const { t, i18n } = useTranslation('checkout');

    const {
        availableShippingMethods,
        selectedMethod,
        summaryMethod,
        defaultShippingMethodId,
        shippingOptionsError,
        isGuest,
        hideChangeForGuest,
        isUpcomingStep,
        getDiscountedPrice,
        handleSubmit,
    } = useShippingOptions({ onSubmit, isLoading, actionData, shippingMethods, isEditing });

    const stepTitle = (
        <span className="text-xl font-bold tracking-tight text-card-foreground">{t('shippingOptions.title')}</span>
    );

    return (
        <ToggleCard
            id="shipping-options"
            title={stepTitle}
            editing={isEditing}
            disabled={false}
            onEdit={onEdit}
            editLabel={t('common.edit')}
            disableEdit={hideChangeForGuest || isUpcomingStep}
            showHeaderSeparator
            isLoading={isLoading}>
            <ToggleCardEdit>
                <form method="post" className="flex flex-col gap-4" onSubmit={handleSubmit}>
                    {shippingOptionsError && <CheckoutErrorBanner message={shippingOptionsError} />}

                    <div className="flex flex-col gap-4">
                        {availableShippingMethods.length > 0 ? (
                            <RadioGroup
                                name="shippingMethodId"
                                defaultValue={selectedMethod?.id || defaultShippingMethodId || ''}
                                required
                                aria-label={t('shippingOptions.title')}
                                className="flex flex-col gap-4">
                                {availableShippingMethods.map((method) => (
                                    <label
                                        key={method.id}
                                        htmlFor={method.id}
                                        className="group flex cursor-pointer flex-col gap-1 rounded-lg border border-border-subtle p-4 transition-all duration-200 has-[[data-state=checked]]:border-foreground">
                                        <div className="flex items-center gap-2">
                                            <RadioGroupItem
                                                value={method.id}
                                                id={method.id}
                                                className="shrink-0"
                                                autoFocus={isEditing && availableShippingMethods.indexOf(method) === 0}
                                            />
                                            <span className="flex-1 text-sm font-medium leading-none">
                                                {method.description || method.name}
                                            </span>
                                            <span className="flex shrink-0 items-center gap-1.5">
                                                {method.shippingPromotions?.length && method.price > 0 ? (
                                                    <>
                                                        <span className="text-sm text-muted-foreground line-through">
                                                            {formatCurrency(method.price, i18n.language, currency)}
                                                        </span>
                                                        <span className="text-sm font-semibold leading-none">
                                                            {getDiscountedPrice(method.price) === 0
                                                                ? t('shippingOptions.free')
                                                                : formatCurrency(
                                                                      getDiscountedPrice(method.price),
                                                                      i18n.language,
                                                                      currency
                                                                  )}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className="text-sm font-semibold leading-none">
                                                        {method.price === 0
                                                            ? t('shippingOptions.free')
                                                            : formatCurrency(method.price, i18n.language, currency)}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        <span className="pl-6 text-sm text-foreground">{method.name}</span>
                                    </label>
                                ))}
                            </RadioGroup>
                        ) : (
                            <div className="flex justify-center rounded-lg border-2 border-dashed border-muted p-8">
                                <div className="space-y-2 text-center">
                                    <Typography variant="p" className="text-muted-foreground">
                                        {t('shippingOptions.noMethodsAvailable')}
                                    </Typography>
                                    <Typography variant="small" className="text-muted-foreground">
                                        {t('shippingOptions.noMethodsAvailableHelp')}
                                    </Typography>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="w-full pt-2">
                        <Button
                            type="submit"
                            disabled={isLoading || availableShippingMethods.length === 0}
                            className="w-full">
                            {isLoading ? t('shippingOptions.saving') : t('shippingOptions.continue')}
                        </Button>
                    </div>
                </form>
            </ToggleCardEdit>

            <ToggleCardSummary>
                {summaryMethod ? (
                    <div className="space-y-1.5">
                        <div className="space-y-1.5">
                            {summaryMethod.description && (
                                <p className="text-sm font-normal leading-5 text-foreground">
                                    {summaryMethod.description}
                                </p>
                            )}
                            <p className="text-sm font-normal leading-5 text-foreground">
                                {summaryMethod.shippingPromotions?.length && summaryMethod.price > 0 ? (
                                    <>
                                        <span className="text-foreground line-through">
                                            {formatCurrency(summaryMethod.price, i18n.language, currency)}
                                        </span>{' '}
                                        {getDiscountedPrice(summaryMethod.price) === 0
                                            ? t('shippingOptions.free')
                                            : formatCurrency(
                                                  getDiscountedPrice(summaryMethod.price),
                                                  i18n.language,
                                                  currency
                                              )}
                                        {' | '}
                                        {summaryMethod.name}
                                    </>
                                ) : (
                                    t('shippingOptions.priceAndMethod', {
                                        price:
                                            summaryMethod.price === 0
                                                ? t('shippingOptions.free')
                                                : formatCurrency(summaryMethod.price ?? 0, i18n.language, currency),
                                        methodName: summaryMethod.name || '',
                                    })
                                )}
                            </p>
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground">
                        {isGuest ? t('shippingOptions.completePreviousSteps') : t('shippingOptions.enterAddressFirst')}
                    </p>
                )}
            </ToggleCardSummary>
        </ToggleCard>
    );
}
